import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Scale, AlertTriangle } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const TermsConditions = () => {
  const navigate = useNavigate();
  const { settings } = useStoreData();
  const { getCartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms & Conditions | Rizzahra"
        description="Read Rizzahra's terms and conditions for using our website and purchasing abayas. Important legal information for customers."
        keywords="terms and conditions, legal, user agreement, purchase terms"
        url="/terms-conditions"
      />
      {/* Main Header */}
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <header className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">Terms & Conditions</h1>
          <p className="text-gray-300 mt-2">Please read these terms carefully before using our services</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Last Updated: February 2025</strong>
            </p>
            <p className="text-gray-700 leading-relaxed">
              Welcome to {settings?.storeName || 'Rizzahra'}. By accessing and using our website, you agree
              to comply with and be bound by the following terms and conditions. Please review these terms
              carefully. If you do not agree with these terms, you should not use this website.
            </p>
          </div>

          {/* Acceptance of Terms */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <FileText className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">Acceptance of Terms</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                By accessing or using our website and services, you acknowledge that you have read, understood,
                and agree to be bound by these Terms and Conditions, as well as our Privacy Policy and any
                other policies posted on our website.
              </p>
              <p>
                We reserve the right to update, change, or replace any part of these Terms and Conditions
                at any time. It is your responsibility to check this page periodically for changes. Your
                continued use of the website following any changes constitutes acceptance of those changes.
              </p>
            </div>
          </div>

          {/* Use of Website */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Use of Website</h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-lg mb-2">Permitted Use</h3>
                <p>You may use our website for:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                  <li>Browsing and purchasing products</li>
                  <li>Creating an account to manage orders</li>
                  <li>Accessing product information and promotions</li>
                  <li>Contacting customer service</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Prohibited Activities</h3>
                <p>You agree NOT to:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                  <li>Use the website for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the website's operation</li>
                  <li>Use automated systems (bots, scrapers) without permission</li>
                  <li>Copy, reproduce, or distribute website content without authorization</li>
                  <li>Post false, misleading, or defamatory content</li>
                  <li>Impersonate another person or entity</li>
                  <li>Violate any applicable local, national, or international law</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Account Registration */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Account Registration</h2>
            <div className="space-y-3 text-gray-700">
              <p>When creating an account, you agree to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information to keep it accurate</li>
                <li>Maintain the security of your password and account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
              <p className="mt-4">
                We reserve the right to suspend or terminate accounts that violate these terms or engage
                in suspicious activity.
              </p>
            </div>
          </div>

          {/* Orders and Payments */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <Scale className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">Orders and Payments</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-lg mb-2">Order Acceptance</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>All orders are subject to acceptance and product availability</li>
                  <li>We reserve the right to refuse or cancel any order</li>
                  <li>Order confirmation does not guarantee acceptance</li>
                  <li>We may contact you to verify order details before processing</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Pricing</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>All prices are in Pakistani Rupees (PKR) unless otherwise stated</li>
                  <li>Prices are subject to change without notice</li>
                  <li>We strive for accuracy but errors may occur; we reserve the right to correct pricing errors</li>
                  <li>Sale prices and promotions are valid for limited periods as specified</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Payment</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Payment must be made before order shipment (except COD)</li>
                  <li>We accept various payment methods as displayed at checkout</li>
                  <li>All payment information is encrypted and securely processed</li>
                  <li>For COD orders, payment is due upon delivery</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Shipping and Delivery */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Shipping and Delivery</h2>
            <div className="space-y-3 text-gray-700">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Delivery timeframes are estimates and not guaranteed</li>
                <li>We are not responsible for delays caused by courier services or unforeseen circumstances</li>
                <li>Risk of loss transfers to you upon delivery</li>
                <li>You are responsible for providing accurate shipping information</li>
                <li>Additional charges may apply for address changes after order placement</li>
              </ul>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Intellectual Property Rights</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                All content on this website, including but not limited to text, graphics, logos, images,
                product descriptions, and software, is the property of {settings?.storeName || 'Rizzahra'}
                or its content suppliers and is protected by intellectual property laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, create derivative works, publicly display,
                republish, or transmit any content from this website without our prior written permission.
              </p>
            </div>
          </div>

          {/* Product Information */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Product Information</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                We strive to provide accurate product information, including descriptions, images, and
                pricing. However:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Colors may vary slightly due to screen settings and lighting</li>
                <li>Product specifications are subject to change without notice</li>
                <li>Images are for illustration purposes and may not represent exact products</li>
                <li>We do not warrant that product descriptions are error-free or complete</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">Limitation of Liability</h2>
            </div>
            <div className="space-y-3 text-gray-700">
              <p>
                To the fullest extent permitted by law, {settings?.storeName || 'Rizzahra'} shall not be
                liable for:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Indirect, incidental, special, or consequential damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Damages arising from website use or inability to use</li>
                <li>Errors, mistakes, or inaccuracies in content</li>
                <li>Unauthorized access to your personal information</li>
              </ul>
              <p className="mt-4">
                Our total liability shall not exceed the amount you paid for the product in question.
              </p>
            </div>
          </div>

          {/* Indemnification */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify and hold harmless {settings?.storeName || 'Rizzahra'}, its officers,
              directors, employees, and agents from any claims, damages, losses, liabilities, and expenses
              (including legal fees) arising from your violation of these Terms and Conditions or your use
              of the website.
            </p>
          </div>

          {/* Governing Law */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Governing Law</h2>
            <p className="text-gray-700">
              These Terms and Conditions are governed by and construed in accordance with the laws of
              Pakistan. Any disputes arising from these terms shall be subject to the exclusive jurisdiction
              of the courts of Pakistan.
            </p>
          </div>

          {/* Severability */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Severability</h2>
            <p className="text-gray-700">
              If any provision of these Terms and Conditions is found to be invalid or unenforceable, the
              remaining provisions shall continue in full force and effect.
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-[#4a1a1f] text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Questions About These Terms?</h3>
            <p className="text-gray-300 mb-6">
              If you have questions or concerns about our Terms and Conditions, please contact us.
            </p>
            <div className="space-y-2 text-gray-300">
              {settings?.contactInfo?.email && (
                <p>Email: {settings.contactInfo.email}</p>
              )}
              {settings?.contactInfo?.phone && (
                <p>Phone: {settings.contactInfo.phone}</p>
              )}
              {settings?.contactInfo?.address && (
                <p>Address: {settings.contactInfo.address}</p>
              )}
            </div>
            <p className="text-gray-400 mt-6 text-sm">
              By using our website, you acknowledge that you have read and understood these Terms and Conditions.
            </p>
          </div>
        </div>
      </div>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default TermsConditions;
